import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TenantService, type Tenant } from '@/services/tenantService';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Building2 } from 'lucide-react';

// Import the regular Index component
import Index from './Index';

export const TenantIndex: React.FC = () => {
  const { subdomain } = useParams<{ subdomain: string }>();
  const { toast } = useToast();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTenant = async () => {
      // Determine subdomain: use route param if present, else parse from hostname
      let effectiveSub = subdomain || '';
      if (!effectiveSub && typeof window !== 'undefined') {
        const host = window.location.hostname;
        const parts = host.split('.');
        if (parts.length >= 3) {
          const first = parts[0];
          // Support both regular subdomains and subdomain.localhost in dev
          if (!['www','api','admin'].includes(first)) {
            effectiveSub = first;
          }
        } else if (host.endsWith('.localhost')) {
          // Explicit support for subdomain.localhost
          effectiveSub = host.split('.')[0];
        }
      }

      if (!effectiveSub) {
        setError('No subdomain provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const tenantData = await TenantService.getTenantBySubdomain(effectiveSub);
        setTenant(tenantData);

        // Apply tenant branding
        applyTenantBranding(tenantData);

      } catch (err) {
        console.error('Error loading tenant:', err);
        setError('Tenant not found');
        toast({
          title: 'Tenant Not Found',
          description: `No tenant found with subdomain "${effectiveSub}"`,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadTenant();
  }, [subdomain, toast]);

  const applyTenantBranding = (tenant: Tenant) => {
    const root = document.documentElement;

    // Apply tenant colors if available
    if (tenant.color_scheme_json && typeof tenant.color_scheme_json === 'object') {
      const colors = tenant.color_scheme_json as Record<string, string>;

      if (colors.primary) {
        // Apply to both standard and tenant-specific CSS variables
        root.style.setProperty('--primary', colors.primary);
        root.style.setProperty('--primary-foreground', '#ffffff');
        root.style.setProperty('--tenant-primary', colors.primary);
      }
      if (colors.secondary) {
        root.style.setProperty('--secondary', colors.secondary);
        root.style.setProperty('--secondary-foreground', '#ffffff');
        root.style.setProperty('--tenant-secondary', colors.secondary);
      }
      if (colors.accent) {
        root.style.setProperty('--accent', colors.accent);
        root.style.setProperty('--accent-foreground', '#ffffff');
        root.style.setProperty('--tenant-accent', colors.accent);
      }
      if (colors.background) {
        root.style.setProperty('--background', colors.background);
        root.style.setProperty('--tenant-background', colors.background);
      }
      if (colors.text) {
        root.style.setProperty('--foreground', colors.text);
        root.style.setProperty('--tenant-text', colors.text);
      }
    }

    // Update page title
    document.title = `${tenant.name} - Business Insurance`;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `Get business insurance quotes from ${tenant.name}. Compare rates and coverage options.`);
    }
  };

  const resetBranding = () => {
    const root = document.documentElement;
    // Reset standard CSS variables
    root.style.removeProperty('--primary');
    root.style.removeProperty('--primary-foreground');
    root.style.removeProperty('--secondary');
    root.style.removeProperty('--secondary-foreground');
    root.style.removeProperty('--accent');
    root.style.removeProperty('--accent-foreground');
    root.style.removeProperty('--background');
    root.style.removeProperty('--foreground');

    // Reset tenant-specific CSS variables
    root.style.removeProperty('--tenant-primary');
    root.style.removeProperty('--tenant-secondary');
    root.style.removeProperty('--tenant-accent');
    root.style.removeProperty('--tenant-background');
    root.style.removeProperty('--tenant-text');

    // Reset page title
    document.title = 'YallaCompare for Business - Business Insurance';
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetBranding();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading {subdomain}...</p>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Tenant Not Found</h1>
          <p className="text-muted-foreground mb-4">
            The tenant "{subdomain}" could not be found or is not available.
          </p>
          <a 
            href="/" 
            className="text-primary hover:underline"
          >
            Return to Main Site
          </a>
        </div>
      </div>
    );
  }

  // Create a tenant context wrapper for the Index component
  return (
    <TenantWrapper tenant={tenant}>
      <Index />
    </TenantWrapper>
  );
};

// Wrapper component to provide tenant context to the Index page
interface TenantWrapperProps {
  tenant: Tenant;
  children: React.ReactNode;
}

const TenantWrapper: React.FC<TenantWrapperProps> = ({ tenant, children }) => {
  // Clone children and inject tenant props if needed
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { tenant } as Record<string, unknown>);
    }
    return child;
  });

  return (
    <div className="tenant-wrapper" data-tenant-id={tenant.id} data-tenant-name={tenant.name}>
      {/* Custom tenant logo in header if available */}
      {tenant.logo_url && !['incorpify-ai','incorpify'].includes(tenant.subdomain) && (
        <style>
          {`
            .tenant-wrapper .logo-container img {
              content: url('${tenant.logo_url}');
            }
          `}
        </style>
      )}
      {childrenWithProps}
    </div>
  );
};

export default TenantIndex;
